import { track } from '@/lib/analytics/track';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import type {
  GdtErrorType,
  GdtPvsSystemName,
  GdtRecordType,
  GdtSafeContext,
  GdtVersion,
} from '@/models/analytics/features/integrations';

import type { GdtConfig } from './types';

export type {
  GdtErrorType,
  GdtPvsSystemName,
  GdtRecordType,
  GdtSafeContext,
  GdtVersion,
} from '@/models/analytics/features/integrations';

type GdtTelemetryConfig = Pick<
  GdtConfig,
  'pvsSystemName' | 'gdtVersion' | 'enableSectionalNotes' | 'outputFilename'
>;

const PVS_SLUGS: Readonly<Record<string, GdtPvsSystemName>> = {
  'cgm albis': 'cgm_albis',
  'cgm m1 pro': 'cgm_m1_pro',
  duria: 'duria',
  duria2: 'duria2',
  'doc cirrus insuite': 'doc_cirrus_insuite',
  medatixx: 'medatixx',
  'medatixx x.comfort': 'medatixx_x_comfort',
  'medatixx x.concept': 'medatixx_x_concept',
  'medatixx x.isynet': 'medatixx_x_isynet',
  'medatixx x.vianova': 'medatixx_x_vianova',
  'medatixx xentro': 'medatixx_xentro',
  'medical office': 'medical_office',
  nephro7: 'nephro7',
  pegamed: 'pegamed',
  t2med: 't2med',
  tomedo: 'tomedo',
  turbomed: 'turbomed',
};

const GDT_PVS_SYSTEM_NAMES: ReadonlySet<GdtPvsSystemName> = new Set([
  ...Object.values(PVS_SLUGS),
  'other',
]);

const GDT_ERROR_TYPES: ReadonlySet<GdtErrorType> = new Set([
  'integration_gdt_file_error',
  'integration_gdt_invalid_line_format',
  'integration_gdt_length_mismatch',
  'integration_gdt_missing_required_field',
  'integration_gdt_encoding_error',
  'integration_gdt_invalid_record_type',
  'integration_gdt_parse_error',
  'integration_gdt_not_enabled',
  'integration_gdt_not_configured',
  'integration_gdt_pdf_error',
  'integration_gdt_watcher_error',
  'integration_gdt_internal',
  'unknown',
]);

function toAllowedPvsSlug(value: unknown): GdtPvsSystemName {
  if (typeof value !== 'string') return 'other';
  const normalized = value.trim().toLowerCase().replace(/\s+/g, ' ');
  return PVS_SLUGS[normalized] ?? 'other';
}

function toAllowedContextPvsSlug(value: unknown): GdtPvsSystemName {
  if (typeof value !== 'string') return 'other';
  const slug = value as GdtPvsSystemName;
  return GDT_PVS_SYSTEM_NAMES.has(slug) ? slug : 'other';
}

function toAllowedGdtVersion(value: unknown): GdtVersion {
  return value === '02.10' || value === '03.50' ? value : 'other';
}

function toAllowedRecordType(value: unknown): GdtRecordType {
  return value === '6300' || value === '6302' || value === '6310' || value === '6311'
    ? value
    : 'unknown';
}

function toAllowedGdtErrorType(value: unknown): GdtErrorType {
  if (typeof value !== 'string') return 'unknown';
  const normalized = value.toLowerCase() as GdtErrorType;
  return GDT_ERROR_TYPES.has(normalized) ? normalized : 'unknown';
}

function toDurationMs(value: number): number {
  return Number.isFinite(value) ? Math.max(0, Math.round(value)) : 0;
}

function enforceSafeGdtContext(context: GdtSafeContext): GdtSafeContext {
  return {
    pvs_system_name: toAllowedContextPvsSlug(context.pvs_system_name),
    gdt_version: toAllowedGdtVersion(context.gdt_version),
    is_sectional_mode: context.is_sectional_mode === true,
    has_fixed_filename: context.has_fixed_filename === true,
  };
}

/**
 * Convert local GDT configuration into the only context shape telemetry accepts.
 * Raw config objects, patient data, note content, IDs, and paths cannot cross
 * this boundary.
 */
export function toSafeGdtContext(config: GdtTelemetryConfig): GdtSafeContext {
  return {
    pvs_system_name: toAllowedPvsSlug(config.pvsSystemName),
    gdt_version: toAllowedGdtVersion(config.gdtVersion),
    is_sectional_mode: config.enableSectionalNotes === true,
    has_fixed_filename: Boolean(config.outputFilename?.trim()),
  };
}

export function trackGdtFileParsed(
  context: GdtSafeContext,
  recordType: unknown = 'unknown'
): void {
  track({
    event_name: TrackingEventName.GdtFileParsed,
    props: {
      ...enforceSafeGdtContext(context),
      record_type: toAllowedRecordType(recordType),
      status: 'success',
    },
    options: { amplitude: false },
  });
}

/**
 * GDT note export success/failure reuse the cross-EHR note_synced/
 * note_sync_failed events instead of bespoke GDT events, so every push-to-EHR
 * integration (Best Practice, Medical Director, Cerner, GDT, ...) is
 * segmentable the same way. `integration_type`/`ehr_provider` identify GDT;
 * the remaining GDT-specific fields are optional on that shared event and
 * stay closed/allowlisted here exactly as they were on the bespoke events.
 */
export function trackGdtNoteExported(
  context: GdtSafeContext,
  exportDurationMs: number,
  sessionId?: string
): void {
  const gdtDimensions = {
    ...enforceSafeGdtContext(context),
    record_type: '6310' as const,
    export_duration_ms: toDurationMs(exportDurationMs),
  };

  track({
    event_name: TrackingEventName.NoteSynced,
    props: {
      ehr_provider: 'gdt',
      integration_type: 'gdt',
      sync_action: 'push_note_to_ehr',
      session_id: sessionId,
      ...gdtDimensions,
      // The note_synced Snowplow schema only declares ehr_provider/
      // integration_type/session_id/sync_action; any other top-level
      // property is undeclared and gets silently dropped at warehouse load
      // (it still reaches Datadog/Amplitude, which store whatever arrives).
      // `details` is a declared field, so nest the GDT dimensions there too
      // — JSON-encoded, matching this schema's `details: string` shape.
      // IntegrationNoteSyncedEventProps types `details` as an object because
      // note_sync_failed's schema declares it that way; note_synced shares
      // the same app-level props type but its own schema wants a string, so
      // assert past the shared type rather than widen it for every caller.
      details: JSON.stringify(gdtDimensions) as unknown as Record<string, unknown>,
    },
    options: { amplitude: false },
  });
}

export function trackGdtExportFailed(
  context: GdtSafeContext,
  exportDurationMs: number,
  errorCode: unknown,
  sessionId?: string
): void {
  const gdtDimensions = {
    ...enforceSafeGdtContext(context),
    record_type: '6310' as const,
    export_duration_ms: toDurationMs(exportDurationMs),
    error_type: toAllowedGdtErrorType(errorCode),
  };

  track({
    event_name: TrackingEventName.NoteSyncFailed,
    props: {
      ehr_provider: 'gdt',
      integration_type: 'gdt',
      sync_action: 'push_note_to_ehr',
      session_id: sessionId,
      ...gdtDimensions,
      // Same undeclared-property caveat as trackGdtNoteExported, but this
      // schema's `details` is already a declared object field (existing
      // note_sync_failed callers pass `{ message }`), so nest it as-is.
      details: gdtDimensions,
    },
    options: { amplitude: false },
  });
}
